
import { CdkConnectedOverlay } from '@angular/cdk/overlay';
import { Component, inject, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ActionItem } from '@next/core-lib/components';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { Store, select } from '@ngrx/store';
import { BehaviorSubject, combineLatest, EMPTY, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, debounceTime, filter, map, startWith, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import {
  SiteState,
  AidAdvice,
  TreatmentWithNitrogenEffectivity,
  ExecutedTreatmentWithNitrogenEffectivity,
  CropFieldAdvice,
} from 'src/app/shared/models';
import {
  GetEnvironmentalImpactReport,
  SetEnvironmentalImpactReport,
  ShowIndex,
} from 'src/app/store/actions';

import {
  EditingTreatmentApi,
  FertilizerReportItemApi,
  FertilizerTable,
} from './models/fertilization-result.model';
import {
  AddTreatment,
  EditTreatment,
  RemoveTreatment,
  CopyTreatmentsTo,
  ClearAdvicePending,
  OpenFertilizationSettingDialog,
  RemoveExecutedTreatment,
  EditExecutedTreatment,
  CopyTreatment,
  OpenSelectSchemaDialog,
  GetPreviousYearsAdvice,
  ToggleAdviceCompleted,
  SelectCustomerForImport,
  CopyTreatmentToOtherAdvicesDialogs,
  CopyExecutedTreatmentToOtherAdvicesDialogs,
} from './store/actions';
import {
  selectEditingAdvice,
  selectExecutedTreatments,
  selectFertilizationSetting,
  selectRecommendedNutrientQuantityWithIds,
  selectTemplateSchemas,
  shouldShowSpidermiteChip,
} from './store/selectors';
import { selectEditingTreatments } from './store/treatment.selectors';
import {
  SalesOrderPeriod,
  TreatmentType,
} from '../sales-order-period/models/sales-order-period.models';
import {
  selectPastSalesOrderPeriodsByType,
  selectSalesOrderPeriodsByType,
} from '../sales-order-period/store/sales-order-period.selectors';
import { ApiService } from '../shared/services/api.service';
import {
  selectCropAidAdviceNamesAndDosages,
  getCropYear,
  getPreviousCropYear,
} from '../store/selectors';

enum ButtonAction {
  settings = 'settings',
  copy = 'copy',
  delete = 'delete',
  applyPreviousYearsAdvice = 'applyPreviousYearsAdvice',
  toggleCompleted = 'toggleCompleted',
  importFromCustomer = 'importFromCustomer',
}

enum TreatmentAction {
  copyToOtherAdvices = 'copyToOtherAdvices',
  copy = 'copy',
  edit = 'edit',
  remove = 'remove',
}

enum ExecutedTreatmentAction {
  copyToOtherAdvices = 'copyToOtherAdvices',
  edit = 'edit',
  remove = 'remove',
}

interface TreatmentWithActions extends TreatmentWithNitrogenEffectivity {
  actions: ActionItem[];
}

interface ExecutedTreatmentWithActions extends ExecutedTreatmentWithNitrogenEffectivity {
  actions: ActionItem[];
}

@Component({
  selector: 'app-advice',
  templateUrl: './advice.component.html',
  styleUrls: ['./advice.component.scss'],
  standalone: false,
})
export class AdviceComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(ApiService);
  private readonly locale = inject(LocaleService);

  @ViewChild('overlayContainer', { read: CdkConnectedOverlay })
    overlayContainerRef: CdkConnectedOverlay;

  destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  cropYear$ = this.store.pipe(select(getCropYear));
  previousCropYear$ = this.store.pipe(select(getPreviousCropYear));
  treatmentType: TreatmentType = this.route.snapshot.url.slice(-1)[0].path as TreatmentType;
  cropFieldAdvice$ = this.store.pipe(select(selectEditingAdvice));
  cropFieldAdvice: CropFieldAdvice;
  adviceCompleted$ = this.cropFieldAdvice$.pipe(
    filter((advice) => advice !== null),
    map((advice) =>
      this.treatmentType === TreatmentType.Fertilization
        ? advice.fertilizationCompleted
        : advice.gbmCompleted,
    ),
  );
  treatments$ = this.store.pipe(select(selectEditingTreatments));
  executedTreatments$ =
    this.treatmentType !== TreatmentType.Fertilization
      ? of([])
      : this.store.pipe(select(selectExecutedTreatments));

  areTreatmentsValid$ = this.cropFieldAdvice$.pipe(
    filter((advice) => advice !== null),
    map(
      (cropFieldAdvice) =>
        (this.treatmentType === TreatmentType.Fertilization &&
          !cropFieldAdvice.fertilizationCorrupt) ||
        (this.treatmentType === TreatmentType.GBM && !cropFieldAdvice.gbmCorrupt),
    ),
  );
  showFertilizationDataDetails$ = new BehaviorSubject(false);
  templateSchemas$ = this.store.pipe(select(selectTemplateSchemas));
  salesOrderPeriods$ = this.store.pipe(
    select(selectSalesOrderPeriodsByType({ periodType: this.treatmentType })),
  );
  pastSalesOrderPeriods$ = this.store.pipe(
    select(selectPastSalesOrderPeriodsByType({ periodType: this.treatmentType })),
  );
  isFertilizationAdvice = false;

  buttonItems$ = this.adviceCompleted$.pipe(
    map((completed) => [
      {
        viewValue: 'finishedAdvice',
        key: ButtonAction.toggleCompleted,
        icon: completed ? 'check_box' : 'check_box_outline_blank',
      },
      ...(this.treatmentType === TreatmentType.Fertilization
        ? [
          {
            icon: 'settings',
            viewValue: 'fertilizationSetting',
            key: ButtonAction.settings,
          },
          {
            icon: 'autorenew',
            viewValue: 'applyPreviousYearsAdvice',
            key: ButtonAction.applyPreviousYearsAdvice,
          },
        ]
        : []),
      {
        icon: 'file_copy',
        viewValue: 'copyTreatments',
        key: ButtonAction.copy,
      },
      {
        icon: 'delete',
        viewValue: 'clearAdvice',
        key: ButtonAction.delete,
      },
      {
        icon: 'get_app',
        viewValue: 'importFromCustomer',
        key: ButtonAction.importFromCustomer,
      },
    ]),
  );

  editingTreatments$ = this.store.pipe(select(selectEditingTreatments));
  recommendedNutrientQuantityWithIds$ = this.store.pipe(
    select(selectRecommendedNutrientQuantityWithIds),
  );
  executedTreatmentsWithActions$ = combineLatest([
    this.areTreatmentsValid$,
    this.executedTreatments$,
  ]).pipe(
    map(([treatmentsValid, executedTreatments]) =>
      [...executedTreatments].map((treatment: ExecutedTreatmentWithActions) => ({
        ...treatment,
        actions: [
          {
            icon: 'unarchive',
            viewValue: 'copyToAdvices',
            key: TreatmentAction.copyToOtherAdvices,
            disabled: !treatmentsValid,
          },
          {
            icon: 'edit',
            viewValue: 'edit',
            key: TreatmentAction.edit,
            disabled: !treatmentsValid,
          },
          {
            icon: 'delete',
            viewValue: 'remove',
            key: TreatmentAction.remove,
            disabled: !treatmentsValid,
          },
        ],
      })),
    ),
  );

  treatmentsWithActions$ = combineLatest([this.areTreatmentsValid$, this.treatments$]).pipe(
    map(([treatmentsValid, treatments]) =>
      [...treatments].map((treatment: TreatmentWithActions) => ({
        ...treatment,
        actions: [
          {
            icon: 'unarchive',
            viewValue: 'copyToAdvices',
            key: TreatmentAction.copyToOtherAdvices,
            disabled: !treatmentsValid && this.isTreatmentValid(treatment),
          },
          {
            icon: 'file_copy',
            viewValue: 'copy',
            key: TreatmentAction.copy,
            disabled: !treatmentsValid,
          },
          {
            icon: 'edit',
            viewValue: 'edit',
            key: TreatmentAction.edit,
            disabled: !treatmentsValid && this.isTreatmentValid(treatment),
          },
          {
            icon: 'delete',
            viewValue: 'remove',
            key: TreatmentAction.remove,
            disabled: !treatmentsValid && this.isTreatmentValid(treatment),
          },
        ],
      })),
    ),
  );
  tableReportData$: BehaviorSubject<FertilizerTable[]> = new BehaviorSubject<FertilizerTable[]>([]);

  fertilizationSetting$ = this.store.pipe(select(selectFertilizationSetting));
  needsSpidermiteAttentionChip$: Observable<boolean>;
  chipList: { class?: string; label: string; condition: boolean; icon?: string; color?: string }[] = [];

  constructor() {}

  ngOnInit() {
    this.isFertilizationAdvice = this.treatmentType === TreatmentType.Fertilization;

    this.cropFieldAdvice$
      .pipe(
        takeUntil(this.destroyed$),
        switchMap((cropFieldAdvice) => {
          this.cropFieldAdvice = cropFieldAdvice;

          // Clear report to erase proviously selected advice's active ingredient amount until this advice's amount has been loaded
          this.store.dispatch(new SetEnvironmentalImpactReport(null));

          if (cropFieldAdvice) {
            this.store.dispatch(
              new GetEnvironmentalImpactReport([cropFieldAdvice.cropFieldId]),
            );
          } else {
            this.tableReportData$.next([]);
          }

          const needsSpidermiteAttentionChip$ = cropFieldAdvice?.agrifirmCropId && cropFieldAdvice?.cropVariety
            ? this.store.pipe(
              select(
                shouldShowSpidermiteChip(
                  cropFieldAdvice.agrifirmCropId,
                  cropFieldAdvice.cropVariety.ediCode,
                ),
              ),
              startWith(false),
            )
            : of(false);

          return combineLatest([of(cropFieldAdvice), needsSpidermiteAttentionChip$]);
        }),
        map(([cropFieldAdvice, needsSpidermiteAttention]) => {
          const chipList: {
            label: string;
            condition: boolean | undefined;
            class?: string;
            icon?: string;
            color?: string;}[]  = [
              { label: 'planetProof', condition: cropFieldAdvice?.isPlanetProof },
              { label: 'organic', condition: cropFieldAdvice?.isSkal },
              { label: 'isGroundWaterPreservation', condition: cropFieldAdvice?.isGroundWaterPreservation },
              { label: 'sandySoil', condition: cropFieldAdvice?.isSandySoil },
              { label: 'isNutrientsPollutedArea', condition: cropFieldAdvice?.isNutrientsPollutedArea },
              { label: 'isGroundWaterPreservationWithNitrogenCuts', condition: cropFieldAdvice?.isGroundWaterPreservationWithNitrogenCuts },
            ];

          // Add spidermite chip if needed
          if (needsSpidermiteAttention) {
            chipList.push({
              label: 'spidermiteAttentionChip',
              condition: true,
              class: 'spidermite-chip',
              icon: 'priority_high',
              color: 'warn',
            });
          }

          return chipList;
        }),
      )
      .subscribe((chipList) => {
        this.chipList = chipList;
      });

    combineLatest([
      this.executedTreatments$,
      this.recommendedNutrientQuantityWithIds$,
      this.editingTreatments$,
    ])
      .pipe(
        takeUntil(this.destroyed$),
        debounceTime(1000),
        withLatestFrom(this.cropYear$),
        map(([[executedTreatments, recommendedNutrientQuantity, editingTreatments], cropYear]) => {
          const adviceId = this.cropFieldAdvice?.adviceId;
          if (!adviceId || adviceId <= 0 || !cropYear) {
            return EMPTY;
          }

          return this.api
            .getFertilizationData(
              cropYear,
              recommendedNutrientQuantity?.find(
                (rn) => rn?.cropFieldId === this.cropFieldAdvice?.cropFieldId,
              ),
              adviceId,
              this.combineTreatments(executedTreatments, editingTreatments),
            )
            .pipe(
              map((result) => this.tableReportData$.next([this.mapApiResultToTableData(result)])),
              catchError((_) => {
                this.store.dispatch(
                  NotificationActions.errorSnackbar({
                    message: this.locale.translate('errorGettingFertilizationData'),
                  }),
                );
                return of({});
              }),
            )
            .subscribe();
        }),
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  buttonAction(data: string) {
    switch (data) {
    case ButtonAction.settings:
      this.openFertilizationSettingDialog();
      break;
    case ButtonAction.applyPreviousYearsAdvice:
      this.applyPreviousYearsAdvice();
      break;
    case ButtonAction.copy:
      this.copyTreatmentsTo();
      break;
    case ButtonAction.delete:
      this.clearAdvice();
      break;
    case ButtonAction.toggleCompleted:
      this.toggleCompleted();
      break;
    case ButtonAction.importFromCustomer:
      this.importAdviceFromCustomer();
      break;
    }
  }

  toggleCompleted() {
    this.store.dispatch(new ToggleAdviceCompleted(this.treatmentType));
  }

  treatmentAction(key: string, { actions, ...treatment }: TreatmentWithActions) {
    switch (key) {
    case TreatmentAction.copy:
      this.copyTreatment(treatment);
      break;
    case TreatmentAction.copyToOtherAdvices:
      this.copyTreatmentToOtherAdvices(treatment);
      break;
    case TreatmentAction.edit:
      this.editTreatment(treatment);
      break;
    case TreatmentAction.remove:
      this.removeTreatment(treatment);
      break;
    }
  }

  executedTreatmentAction(key: string, { actions, ...treatment }: ExecutedTreatmentWithActions) {
    switch (key) {
    case ExecutedTreatmentAction.copyToOtherAdvices:
      this.copyExecutedTreatmentToOtherAdvices(treatment);
      break;
    case ExecutedTreatmentAction.edit:
      this.editExecutedTreatment(treatment);
      break;
    case TreatmentAction.remove:
      this.removeExecutedTreatment(treatment.id);
      break;
    }
  }

  fertilizationDetailsShown() {
    const element: HTMLElement = this.overlayContainerRef.overlayRef.overlayElement;
    const rect = element.getBoundingClientRect();
    this.overlayContainerRef.overlayRef.updateSize({ width: rect.width });
  }

  showFertilizationDetails() {
    this.showFertilizationDataDetails$.next(true);
  }

  hideFertilizationDetails() {
    this.showFertilizationDataDetails$.next(false);
  }

  getCropAidAdviceNamesAndDosages$(aidAdvices: AidAdvice[]) {
    return this.store.pipe(select(selectCropAidAdviceNamesAndDosages(aidAdvices)));
  }

  openOverview() {
    this.store.dispatch(new ShowIndex());
  }

  addTreatment() {
    this.store.dispatch(new AddTreatment(this.treatmentType));
  }

  copyTreatment(treatment: TreatmentWithNitrogenEffectivity) {
    this.store.dispatch(
      new CopyTreatment({
        ...treatment,
        id: '',
      }),
    );
  }

  copyTreatmentToOtherAdvices(treatment: TreatmentWithNitrogenEffectivity) {
    this.store.dispatch(
      new CopyTreatmentToOtherAdvicesDialogs({ ...treatment }, this.treatmentType),
    );
  }

  editTreatment(treatment: TreatmentWithNitrogenEffectivity) {
    this.store.dispatch(
      new EditTreatment(this.treatmentType, treatment.isAnExecutedTreatment, treatment),
    );
  }

  copyExecutedTreatmentToOtherAdvices(treatment: ExecutedTreatmentWithNitrogenEffectivity) {
    this.store.dispatch(
      new CopyExecutedTreatmentToOtherAdvicesDialogs({ ...treatment }, this.treatmentType),
    );
  }

  editExecutedTreatment(treatment: ExecutedTreatmentWithNitrogenEffectivity) {
    this.store.dispatch(new EditExecutedTreatment(this.treatmentType, treatment));
  }

  removeTreatment(treatment: TreatmentWithNitrogenEffectivity) {
    this.store.dispatch(new RemoveTreatment(treatment));
  }

  removeExecutedTreatment(id: number) {
    this.store.dispatch(new RemoveExecutedTreatment(id));
  }

  applyPreviousYearsAdvice() {
    this.store.dispatch(new GetPreviousYearsAdvice());
  }

  copyTreatmentsTo() {
    this.store.dispatch(new CopyTreatmentsTo(this.treatmentType));
  }

  importAdviceFromCustomer() {
    this.store.dispatch(new SelectCustomerForImport());
  }

  selectSchema() {
    this.store.dispatch(new OpenSelectSchemaDialog());
  }

  clearAdvice() {
    this.store.dispatch(new ClearAdvicePending(this.treatmentType));
  }

  getSalesOrderPeriod(
    salesOrderPeriods: SalesOrderPeriod[],
    pastSalesOrderPeriods: SalesOrderPeriod[],
    periodId: number,
  ) {
    return [...salesOrderPeriods, ...pastSalesOrderPeriods].find(
      (salesOrderPeriod) => salesOrderPeriod.id === periodId,
    );
  }

  openFertilizationSettingDialog() {
    this.store.dispatch(new OpenFertilizationSettingDialog());
  }

  isFirstTreatmentInPeriod(
    treatment: TreatmentWithActions | ExecutedTreatmentWithActions,
    treatments: TreatmentWithActions[] | ExecutedTreatmentWithActions[],
    index: number,
  ) {
    return !treatments[index - 1] || treatment.periodId !== treatments[index - 1].periodId;
  }

  hasDivergentHectares(areaInHectaresManual: number, cropFieldAdvice?: CropFieldAdvice) {
    return !cropFieldAdvice ? false : areaInHectaresManual !== cropFieldAdvice.areaInHectares;
  }

  isTreatmentValid = (treatment: TreatmentWithActions): boolean =>
    treatment.isCorrupt !== true &&
    (this.cropFieldAdvice.isPlanetProof !== true || treatment.forbiddenForPlanetProof !== true);

  private combineTreatments(
    executedTreatments: ExecutedTreatmentWithNitrogenEffectivity[],
    editingTreatments: TreatmentWithNitrogenEffectivity[],
  ): EditingTreatmentApi[] {
    return [
      ...executedTreatments.map((t) => ({
        ...t,
        isPrimary: false,
      })),
      ...editingTreatments.map((t) => ({
        ...t,
        isPrimary: false,
      })),
    ];
  }

  private mapApiResultToTableData(item: FertilizerReportItemApi) {
    return {
      metaData: {
        name: item.name,
        cropName: item.cropName,
        cropVariety: item.cropVariety,
        area: item.area,
      },
      fertilizationItems: item.fertilizationItems.map((fItem) => ({
        source: fItem.source,
        data: fItem.fertilizerData,
        primary: fItem.primary,
        isExecuted: fItem.isExecuted,
        note: fItem.note,
        displayDosage: fItem.displayDosage,
      })),
    };
  }
}
